'use client';

import { useTranslations } from 'next-intl';

import { ButtonLink } from '@commons/components/Button';
import DrupalImage from '@commons/components/Image/Drupal';
import RichText from '@commons/components/RichText';
import Separator from '@commons/components/Separator';
import Text from '@commons/components/Text';
import Texture from '@commons/components/Texture';
import Flex from '@commons/containers/Flex';
import ResponsiveContainer from '@commons/containers/ResponsiveContainer';
import { useMediaQueries } from '@commons/contexts/responsive';
import type { DrupalImage as DrupalImageType } from '@commons/types/drupal';

import type { IntelligenceNodeProps } from '.';
import styles from './Featured.module.scss';

// Extending the intelligence node props to include additional fields.

type Props = IntelligenceNodeProps & {
  featuredContent?: string;
  featuredMedia?: DrupalImageType;
  mobileFeaturedMedia?: DrupalImageType;
};

/**
 * Intelligence node component for the Featured display type.
 */

const IntelligenceNodeFeatured = (props: Props) => {
  const { ready, minWidth } = useMediaQueries();

  const isMobile = ready && !minWidth['md'];

  const { image, featuredMedia, mobileFeaturedMedia } = props;

  const background = featuredMedia || image;
  const renderedBackground = background && (
    <div className={styles.background}>
      {isMobile ? <Texture /> : <DrupalImage fill {...background} />}
    </div>
  );

  const mobileMedia = mobileFeaturedMedia || image;
  const renderedMobileMedia = isMobile && mobileMedia && (
    <DrupalImage {...mobileMedia} className={styles.mobileMedia} />
  );

  return (
    <ResponsiveContainer
      theme="black"
      paddingTop="xl"
      paddingBottom="xl"
      outsideChildren={renderedBackground}
    >
      <Content {...props} />
      {renderedMobileMedia}
    </ResponsiveContainer>
  );
};

/**
 * Intelligence node featured display content component.
 *
 * Handles the conditional rendering of the content based on the available fields.
 */

const Content = ({ title, summary, featuredContent, alias }: Props) => {
  const t = useTranslations('commons');

  if (featuredContent) {
    return <RichText value={featuredContent} width={{ xs: 12, md: 6, lg: 4 }} />;
  }

  return (
    <Flex gap="md" width={{ xs: 12, md: 6, lg: 4 }} direction="column">
      <Separator dash color="primary" marginBottom="none" />
      <Text as="h2" transform="uppercase" marginBottom="none">
        {title}
      </Text>
      {summary && (
        <Text level="body-lg" marginBottom="none">
          {summary}
        </Text>
      )}
      <ButtonLink href={alias} variant="primary">
        {t('learnMore')}
      </ButtonLink>
    </Flex>
  );
};

export default IntelligenceNodeFeatured;
